
import { useState, useEffect } from "react";
import axios from "axios";

const SearchComponent = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [employees, setEmployees] = useState([]);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const handleSearch = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}search`, {
        params: { term: searchTerm }
      });
      setEmployees(response.data);
    } catch (error) {
      console.error('Error fetching employees:', error);
    }
  };

  const openModal = (employee) => {
    setSelectedEmployee(employee);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setSelectedEmployee(null);
    setIsModalOpen(false);
  };

  // Helper function to format dates
  const formatDate = (date) => {
    if (!date) return 'N/A';
    try {
      return new Date(date).toISOString().slice(0, 10);
    } catch {
      return 'Invalid Date';
    }
  };

  return (
    <div className="container mx-auto mt-20 p-4">
      <h1 className="text-2xl font-bold mb-4"> Search for employee</h1>
      <div className="flex mb-4">
        <input
          type="text"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder="Search by name or email"
          className="border p-2 rounded-l w-full"
        />
        <button
          onClick={handleSearch}
          className="bg-blue-500 text-white p-2 rounded-r hover:bg-blue-600"
        >
          Search
        </button>
      </div>

      <div className="grid gap-4">
        {employees.map(employee => (
          <div
            key={employee._id}
            className="border p-4 rounded shadow hover:shadow-lg cursor-pointer"
            onClick={() => openModal(employee)}
          >
            <h2 className="text-lg font-semibold">{employee.firstName} {employee.lastName}</h2>
            <p>Email: {employee.email}</p>
            <p>Status: {employee.status}</p>
          </div>
        ))}
      </div>

      {isModalOpen && selectedEmployee && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg max-w-3xl w-full max-h-[80vh] overflow-y-auto">
            <h2 className="text-xl font-bold mb-4">{selectedEmployee.firstName} {selectedEmployee.lastName}</h2>
            <div className="grid gap-2">
              {/* Employee Details */}
              <h3 className="text-lg font-semibold mt-4">Employee Details</h3>
              <p><strong>Email:</strong> {selectedEmployee.email}</p>
              <p><strong>Gender:</strong> {selectedEmployee.gender}</p>
              {selectedEmployee.middleName && <p><strong>Middle Name:</strong> {selectedEmployee.middleName}</p>}
              {selectedEmployee.dateOfBirth && <p><strong>Date of Birth:</strong> {selectedEmployee.dateOfBirth}</p>}
              {selectedEmployee.address && <p><strong>Address:</strong> {selectedEmployee.address}</p>}
              {selectedEmployee.TINType && <p><strong>TIN Type:</strong> {selectedEmployee.TINType}</p>}
              {selectedEmployee.TIN !== 0 && <p><strong>TIN:</strong> {selectedEmployee.TIN}</p>}
              {selectedEmployee.phoneNumber && <p><strong>Phone:</strong> {selectedEmployee.phoneNumber}</p>}
              {selectedEmployee.whatsappNumber && <p><strong>WhatsApp:</strong> {selectedEmployee.whatsappNumber}</p>}
              <p><strong>Status:</strong> {selectedEmployee.status}</p>
              {selectedEmployee.terminationReason && (
                <p><strong>Termination Reason:</strong> {selectedEmployee.terminationReason}</p>
              )}
              <p><strong>Created At:</strong> {formatDate(selectedEmployee.createdAt)}</p>
              <p><strong>Updated At:</strong> {formatDate(selectedEmployee.updatedAt)}</p>

              {/* Salary Details */}
              <h3 className="text-lg font-semibold mt-4">Salary Details</h3>
              <p><strong>Gross Salary:</strong> {selectedEmployee.salary.gross}</p>
              <p><strong>Net Salary:</strong> {selectedEmployee.salary.net}</p>
              <h4 className="font-medium">Deductions:</h4>
              <ul className="list-disc pl-5">
                <li>Tax: {selectedEmployee.salary.deductions.tax}</li>
                <li>Penalty: {selectedEmployee.salary.deductions.penalty}</li>
                <li>HMO: {selectedEmployee.salary.deductions.hmo}</li>
                <li>Other: {selectedEmployee.salary.deductions.other}</li>
              </ul>

              {/* Salary History */}
              <h3 className="text-lg font-semibold mt-4">Salary History</h3>
              {selectedEmployee.salary.history.length > 0 ? (
                <ul className="list-disc pl-5">
                  {selectedEmployee.salary.history.map((hist, index) => (
                    <li key={index}>
                      {hist.month} {hist.year}: Gross {hist.grossSalary}, Net {hist.netSalary}
                      <ul className="list-circle pl-5">
                        <li>Tax: {hist.deductions.tax}</li>
                        <li>Penalty: {hist.deductions.penalty}</li>
                        <li>HMO: {hist.deductions.hmo}</li>
                        <li>Other: {hist.deductions.other}</li>
                      </ul>
                    </li>
                  ))}
                </ul>
              ) : (
                <p>No salary history available.</p>
              )}

              {/* Leave Requests */}
              <h3 className="text-lg font-semibold mt-4">Leave Requests</h3>
              {selectedEmployee.leaveRequests.length > 0 ? (
                <ul className="list-disc pl-5">
                  {selectedEmployee.leaveRequests.map((leave, index) => (
                    <li key={index}>
                      {formatDate(leave.startDate)} to {formatDate(leave.endDate)}: {leave.reason} ({leave.status})
                      {leave.others && <p>Others: {leave.others}</p>}
                      {leave.response && <p>Response: {leave.response}</p>}
                    </li>
                  ))}
                </ul>
              ) : (
                <p>No leave requests available.</p>
              )}

              {/* Queries */}
              <h3 className="text-lg font-semibold mt-4">Queries</h3>
              {selectedEmployee.queries.length > 0 ? (
                <ul className="list-disc pl-5">
                  {selectedEmployee.queries.map((query, index) => (
                    <li key={index}>
                      {formatDate(query.dateIssued)}: {query.description}
                      {query.response && <p>Response: {query.response} ({formatDate(query.responseDate)})</p>}
                    </li>
                  ))}
                </ul>
              ) : (
                <p>No queries available.</p>
              )}

              {/* Attendance */}
              <h3 className="text-lg font-semibold mt-4">Attendance</h3>
              {selectedEmployee.attendance.length > 0 ? (
                <ul className="list-disc pl-5">
                  {selectedEmployee.attendance.map((att, index) => (
                    <li key={index}>
                      {formatDate(att.date)}: {att.status}
                      {att.notes && <p>Notes: {att.notes}</p>}
                    </li>
                  ))}
                </ul>
              ) : (
                <p>No attendance records available.</p>
              )}

              {/* Remarks */}
              <h3 className="text-lg font-semibold mt-4">Remarks</h3>
              {selectedEmployee.remarks.length > 0 ? (
                <ul className="list-disc pl-5">
                  {selectedEmployee.remarks.map((remark, index) => (
                    <li key={index}>
                      {remark.month} {remark.year}: {remark.comment}
                    </li>
                  ))}
                </ul>
              ) : (
                <p>No remarks available.</p>
              )}

              {/* Employer Details */}
              {selectedEmployee.employer && (
                <>
                  <h3 className="text-lg font-semibold mt-4">Employer Details</h3>
                  <p><strong>Company Name:</strong> {selectedEmployee.employer.companyName}</p>
                  <p><strong>Email:</strong> {selectedEmployee.employer.email}</p>
                  <p><strong>Address:</strong> {selectedEmployee.employer.address}</p>
                  <p><strong>State:</strong> {selectedEmployee.employer.state}</p>
                  <p><strong>Local Government:</strong> {selectedEmployee.employer.localGovernment}</p>
                  <p><strong>Country:</strong> {selectedEmployee.employer.country}</p>
                  <p><strong>Phone Number:</strong> {selectedEmployee.employer.phoneNumber}</p>
                  <p><strong>Employer Type:</strong> {selectedEmployee.employer.employerType}</p>
                  <p><strong>User Type:</strong> {selectedEmployee.employer.userType}</p>
                  <p><strong>Unique Number:</strong> {selectedEmployee.employer.uniqueNumber}</p>
                  <p><strong>Is Verified:</strong> {selectedEmployee.employer.isVerified ? 'Yes' : 'No'}</p>
                  <p><strong>Is Blacklisted:</strong> {selectedEmployee.employer.isBlacklisted ? 'Yes' : 'No'}</p>
                  <p><strong>Last Login:</strong> {formatDate(selectedEmployee.employer.lastLogin)}</p>
                  <p><strong>Created At:</strong> {formatDate(selectedEmployee.employer.createdAt)}</p>
                  <p><strong>Updated At:</strong> {formatDate(selectedEmployee.employer.updatedAt)}</p>
                  {selectedEmployee.employer.avatar && <p><strong>Avatar:</strong> {selectedEmployee.employer.avatar}</p>}
                  {selectedEmployee.employer.size && <p><strong>Company Size:</strong> {selectedEmployee.employer.size}</p>}
                  {selectedEmployee.employer.emailOtp && <p><strong>Email OTP:</strong> {selectedEmployee.employer.emailOtp}</p>}
                  {selectedEmployee.employer.otpExpiresAt && (
                    <p><strong>OTP Expires At:</strong> {formatDate(selectedEmployee.employer.otpExpiresAt)}</p>
                  )}
                  {selectedEmployee.employer.resetPasswordToken && (
                    <p><strong>Reset Password Token:</strong> {selectedEmployee.employer.resetPasswordToken}</p>
                  )}
                  {selectedEmployee.employer.resetPaswordExpiresAt && (
                    <p><strong>Reset Password Expires At:</strong> {formatDate(selectedEmployee.employer.resetPaswordExpiresAt)}</p>
                  )}
                  {selectedEmployee.employer.listedJobs?.length > 0 && (
                    <p><strong>Listed Jobs:</strong> {selectedEmployee.employer.listedJobs.length} job(s)</p>
                  )}
                  {selectedEmployee.employer.jobOffers?.length > 0 && (
                    <p><strong>Job Offers:</strong> {selectedEmployee.employer.jobOffers.length} offer(s)</p>
                  )}
                  {selectedEmployee.employer.notifications?.length > 0 && (
                    <p><strong>Notifications:</strong> {selectedEmployee.employer.notifications.length} notification(s)</p>
                  )}
                  {selectedEmployee.employer.reportsMade?.length > 0 && (
                    <p><strong>Reports Made:</strong> {selectedEmployee.employer.reportsMade.length} report(s)</p>
                  )}
                  {selectedEmployee.employer.reportsGotten?.length > 0 && (
                    <p><strong>Reports Gotten:</strong> {selectedEmployee.employer.reportsGotten.length} report(s)</p>
                  )}
                  {selectedEmployee.employer.servicesRendered?.length > 0 && (
                    <p><strong>Services Rendered:</strong> {selectedEmployee.employer.servicesRendered.length} service(s)</p>
                  )}
                </>
              )}

              {/* Job Seeker Details */}
              {selectedEmployee.jobSeeker && (
                <>
                  <h3 className="text-lg font-semibold mt-4">Job Seeker Details</h3>
                  <p><strong>First Name:</strong> {selectedEmployee.jobSeeker.firstName}</p>
                  <p><strong>Last Name:</strong> {selectedEmployee.jobSeeker.lastName}</p>
                  <p><strong>Email:</strong> {selectedEmployee.jobSeeker.email}</p>
                  <p><strong>Gender:</strong> {selectedEmployee.jobSeeker.gender}</p>
                  <p><strong>Address:</strong> {selectedEmployee.jobSeeker.address}</p>
                  <p><strong>State:</strong> {selectedEmployee.jobSeeker.state}</p>
                  <p><strong>Local Government:</strong> {selectedEmployee.jobSeeker.localGovernment}</p>
                  <p><strong>Country:</strong> {selectedEmployee.jobSeeker.country}</p>
                  <p><strong>Phone Number:</strong> {selectedEmployee.jobSeeker.phoneNumber}</p>
                  <p><strong>User Type:</strong> {selectedEmployee.jobSeeker.userType}</p>
                  <p><strong>Unique Number:</strong> {selectedEmployee.jobSeeker.uniqueNumber}</p>
                  <p><strong>Is Verified:</strong> {selectedEmployee.jobSeeker.isVerified ? 'Yes' : 'No'}</p>
                  <p><strong>Is Blacklisted:</strong> {selectedEmployee.jobSeeker.isBlacklisted ? 'Yes' : 'No'}</p>
                  <p><strong>Is NIN Verified:</strong> {selectedEmployee.jobSeeker.isNINVerified ? 'Yes' : 'No'}</p>
                  <p><strong>Last Login:</strong> {formatDate(selectedEmployee.jobSeeker.lastLogin)}</p>
                  <p><strong>Created At:</strong> {formatDate(selectedEmployee.jobSeeker.createdAt)}</p>
                  <p><strong>Updated At:</strong> {formatDate(selectedEmployee.jobSeeker.updatedAt)}</p>
                  {selectedEmployee.jobSeeker.dateOfBirth && (
                    <p><strong>Date of Birth:</strong> {selectedEmployee.jobSeeker.dateOfBirth}</p>
                  )}
                  {selectedEmployee.jobSeeker.avatar && <p><strong>Avatar:</strong> {selectedEmployee.jobSeeker.avatar}</p>}
                  {selectedEmployee.jobSeeker.whatsappNumber && (
                    <p><strong>WhatsApp:</strong> {selectedEmployee.jobSeeker.whatsappNumber}</p>
                  )}
                  {selectedEmployee.jobSeeker.anyChild && <p><strong>Any Children:</strong> {selectedEmployee.jobSeeker.anyChild}</p>}
                  {selectedEmployee.jobSeeker.criminalRecord && (
                    <p><strong>Criminal Record:</strong> {selectedEmployee.jobSeeker.criminalRecord}</p>
                  )}
                  {selectedEmployee.jobSeeker.handworkSkill && (
                    <p><strong>Handwork Skill:</strong> {selectedEmployee.jobSeeker.handworkSkill}</p>
                  )}
                  {selectedEmployee.jobSeeker.specifyHandworkSkill && (
                    <p><strong>Specify Handwork Skill:</strong> {selectedEmployee.jobSeeker.specifyHandworkSkill}</p>
                  )}
                  {selectedEmployee.jobSeeker.instagramHandle && (
                    <p><strong>Instagram Handle:</strong> {selectedEmployee.jobSeeker.instagramHandle}</p>
                  )}
                  {selectedEmployee.jobSeeker.tiktokHandle && (
                    <p><strong>TikTok Handle:</strong> {selectedEmployee.jobSeeker.tiktokHandle}</p>
                  )}
                  {selectedEmployee.jobSeeker.jobDecision && (
                    <p><strong>Job Decision:</strong> {selectedEmployee.jobSeeker.jobDecision}</p>
                  )}
                  {selectedEmployee.jobSeeker.jobDecisionSalary && (
                    <p><strong>Job Decision Salary:</strong> {selectedEmployee.jobSeeker.jobDecisionSalary}</p>
                  )}
                  {selectedEmployee.jobSeeker.leastSalary && (
                    <p><strong>Least Salary:</strong> {selectedEmployee.jobSeeker.leastSalary}</p>
                  )}
                  {selectedEmployee.jobSeeker.minimumSalary && (
                    <p><strong>Minimum Salary:</strong> {selectedEmployee.jobSeeker.minimumSalary}</p>
                  )}
                  {selectedEmployee.jobSeeker.landlordNumber && (
                    <p><strong>Landlord Number:</strong> {selectedEmployee.jobSeeker.landlordNumber}</p>
                  )}
                  {selectedEmployee.jobSeeker.liveAlone && <p><strong>Live Alone:</strong> {selectedEmployee.jobSeeker.liveAlone}</p>}
                  {selectedEmployee.jobSeeker.whoDoYouLiveWith && (
                    <p><strong>Who Do You Live With:</strong> {selectedEmployee.jobSeeker.whoDoYouLiveWith}</p>
                  )}
                  {selectedEmployee.jobSeeker.numberOfChildren && (
                    <p><strong>Number of Children:</strong> {selectedEmployee.jobSeeker.numberOfChildren}</p>
                  )}
                  {selectedEmployee.jobSeeker.numberOfPeople && (
                    <p><strong>Number of People:</strong> {selectedEmployee.jobSeeker.numberOfPeople}</p>
                  )}
                  {selectedEmployee.jobSeeker.numberOfRooms && (
                    <p><strong>Number of Rooms:</strong> {selectedEmployee.jobSeeker.numberOfRooms}</p>
                  )}
                  {selectedEmployee.jobSeeker.registeredEverify && (
                    <p><strong>Registered E-Verify:</strong> {selectedEmployee.jobSeeker.registeredEverify}</p>
                  )}
                  {selectedEmployee.jobSeeker.bankAcctName && (
                    <p><strong>Bank Account Name:</strong> {selectedEmployee.jobSeeker.bankAcctName}</p>
                  )}
                  {selectedEmployee.jobSeeker.bankAcctNum && (
                    <p><strong>Bank Account Number:</strong> {selectedEmployee.jobSeeker.bankAcctNum}</p>
                  )}
                  {selectedEmployee.jobSeeker.bankName && <p><strong>Bank Name:</strong> {selectedEmployee.jobSeeker.bankName}</p>}
                  {selectedEmployee.jobSeeker.nextOfKin && (
                    <p><strong>Next of Kin:</strong> {selectedEmployee.jobSeeker.nextOfKin}</p>
                  )}
                  {selectedEmployee.jobSeeker.nextOfKinAddress && (
                    <p><strong>Next of Kin Address:</strong> {selectedEmployee.jobSeeker.nextOfKinAddress}</p>
                  )}
                  {selectedEmployee.jobSeeker.nextOfKinNumber && (
                    <p><strong>Next of Kin Number:</strong> {selectedEmployee.jobSeeker.nextOfKinNumber}</p>
                  )}
                  {selectedEmployee.jobSeeker.nextOfKinRelationship && (
                    <p><strong>Next of Kin Relationship:</strong> {selectedEmployee.jobSeeker.nextOfKinRelationship}</p>
                  )}
                  {selectedEmployee.jobSeeker.nin && <p><strong>NIN:</strong> {selectedEmployee.jobSeeker.nin}</p>}
                  {selectedEmployee.jobSeeker.residentialCard && (
                    <p><strong>Residential Card:</strong> {selectedEmployee.jobSeeker.residentialCard}</p>
                  )}
                  {selectedEmployee.jobSeeker.scanningEvidence && (
                    <p><strong>Scanning Evidence:</strong> {selectedEmployee.jobSeeker.scanningEvidence}</p>
                  )}
                  {selectedEmployee.jobSeeker.tenancyAgreement && (
                    <p><strong>Tenancy Agreement:</strong> {selectedEmployee.jobSeeker.tenancyAgreement}</p>
                  )}
                  {selectedEmployee.jobSeeker.votersCard && (
                    <p><strong>Voter's Card:</strong> {selectedEmployee.jobSeeker.votersCard}</p>
                  )}
                  {selectedEmployee.jobSeeker.jobMode && <p><strong>Job Mode:</strong> {selectedEmployee.jobSeeker.jobMode}</p>}
                  {selectedEmployee.jobSeeker.availabilityForUrgentWork && (
                    <p><strong>Availability for Urgent Work:</strong> {selectedEmployee.jobSeeker.availabilityForUrgentWork ? 'Yes' : 'No'}</p>
                  )}
                  {selectedEmployee.jobSeeker.isCertified && (
                    <p><strong>Is Certified:</strong> {selectedEmployee.jobSeeker.isCertified ? 'Yes' : 'No'}</p>
                  )}
                  {selectedEmployee.jobSeeker.certificationDate && (
                    <p><strong>Certification Date:</strong> {formatDate(selectedEmployee.jobSeeker.certificationDate)}</p>
                  )}
                  {selectedEmployee.jobSeeker.educationLevel && (
                    <p><strong>Education Level:</strong> {selectedEmployee.jobSeeker.educationLevel}</p>
                  )}
                  {selectedEmployee.jobSeeker.maritalStatus && (
                    <p><strong>Marital Status:</strong> {selectedEmployee.jobSeeker.maritalStatus}</p>
                  )}
                  {selectedEmployee.jobSeeker.accountBalance !== undefined && (
                    <p><strong>Account Balance:</strong> {selectedEmployee.jobSeeker.accountBalance}</p>
                  )}
                  {selectedEmployee.jobSeeker.education?.length > 0 && (
                    <p><strong>Education Records:</strong> {selectedEmployee.jobSeeker.education.length} record(s)</p>
                  )}
                  {selectedEmployee.jobSeeker.workExperience?.length > 0 && (
                    <p><strong>Work Experience:</strong> {selectedEmployee.jobSeeker.workExperience.length} record(s)</p>
                  )}
                  {selectedEmployee.jobSeeker.certificates?.length > 0 && (
                    <p><strong>Certificates:</strong> {selectedEmployee.jobSeeker.certificates.length} certificate(s)</p>
                  )}
                  {selectedEmployee.jobSeeker.guarantors?.length > 0 && (
                    <p><strong>Guarantors:</strong> {selectedEmployee.jobSeeker.guarantors.length} guarantor(s)</p>
                  )}
                  {selectedEmployee.jobSeeker.hiredHistory?.length > 0 && (
                    <p><strong>Hired History:</strong> {selectedEmployee.jobSeeker.hiredHistory.length} record(s)</p>
                  )}
                  {selectedEmployee.jobSeeker.reportsMade?.length > 0 && (
                    <p><strong>Reports Made:</strong> {selectedEmployee.jobSeeker.reportsMade.length} report(s)</p>
                  )}
                  {selectedEmployee.jobSeeker.reportsGotten?.length > 0 && (
                    <p><strong>Reports Gotten:</strong> {selectedEmployee.jobSeeker.reportsGotten.length} report(s)</p>
                  )}
                  {selectedEmployee.jobSeeker.benefits?.length > 0 && (
                    <p><strong>Benefits:</strong> {selectedEmployee.jobSeeker.benefits.length} benefit(s)</p>
                  )}
                  {selectedEmployee.jobSeeker.jobOffers?.length > 0 && (
                    <p><strong>Job Offers:</strong> {selectedEmployee.jobSeeker.jobOffers.length} offer(s)</p>
                  )}
                  {selectedEmployee.jobSeeker.appliedJobs?.length > 0 && (
                    <p><strong>Applied Jobs:</strong> {selectedEmployee.jobSeeker.appliedJobs.length} job(s)</p>
                  )}
                  {selectedEmployee.jobSeeker.notifications?.length > 0 && (
                    <p><strong>Notifications:</strong> {selectedEmployee.jobSeeker.notifications.length} notification(s)</p>
                  )}
                  {selectedEmployee.jobSeeker.interestedJob?.length > 0 && (
                    <p><strong>Interested Jobs:</strong> {selectedEmployee.jobSeeker.interestedJob.length} job(s)</p>
                  )}
                  {selectedEmployee.jobSeeker.resume?.length > 0 && (
                    <p><strong>Resumes:</strong> {selectedEmployee.jobSeeker.resume.length} resume(s)</p>
                  )}
                  {selectedEmployee.jobSeeker.jobChoice?.length > 0 && (
                    <p><strong>Job Choices:</strong> {selectedEmployee.jobSeeker.jobChoice.length} choice(s)</p>
                  )}
                  {selectedEmployee.jobSeeker.servicesRendered?.length > 0 && (
                    <p><strong>Services Rendered:</strong> {selectedEmployee.jobSeeker.servicesRendered.length} service(s)</p>
                  )}
                </>
              )}
            </div>
            <button
              onClick={closeModal}
              className="mt-4 bg-red-500 text-white p-2 rounded hover:bg-red-600"
            >
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default SearchComponent;





























